/* eslint-disable arrow-body-style */
/* eslint-disable camelcase */
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Slug from '../../../../components/slug/slug';
import Card from '../../../../components/eventsHub/organisms/Card';
import FeatureCard from '../../../../components/eventsHub/organisms/FeatureCard';
import CardList from '../../../../components/eventsHub/organisms/CardList';
import Badge from '../../../../components/eventsHub/atoms/Badge';
import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utcPlugin from 'dayjs/plugin/utc';
import Title from '../../../../components/eventsHub/atoms/text/Title';
import { getEyebrowFromTags } from '../utils';

const pageTitle = 'Events';
const pageDescription = `Fast Company events bring together innovative thought leaders, industry disruptors, and creative minds 
  to explore cutting-edge ideas and trends shaping the future of business and technology. 
  These dynamic gatherings provide a platform for networking, collaboration, and insightful 
  discussions that inspire attendees to drive positive change in their respective fields.`;

const CalendarPageComponent = ({ events, pastEvents }) => {
  const featuredEvents = [];
  const upcomingEvents = [];

  // Use custom field for event name if it exists.
  // It's an optional field in Swoogo so we can't rely on it.
  const getEventName = event => event?.c_6615 || event.name;

  function eventHasFeaturedTag(event) {
    // c_72033 is the id of the custom field in swoogo
    return Array.isArray(event?.c_72033) && event.c_72033[0]?.value === 'Yes';
  }

  function eventIsUpcoming(event) {
    const currentEpoch = Date.now();
    const eventEndEpoch = event?.endDateUtc
      ? new Date(event.endDateUtc).getTime()
      : 0;

    return eventEndEpoch > currentEpoch;
  }

  // Segregate events into featured, upcoming, and past
  events.forEach(event => {
    if (featuredEvents.length === 0 && eventHasFeaturedTag(event)) {
      featuredEvents.push(event);
    } else if (eventIsUpcoming(event)) {
      upcomingEvents.push(event);
    }
  });

  const buildEventCta = event => {
    const ctaText = event?.c_70549;
    const ctaDomain = event?.domain;
    const ctaSlug = event?.url;

    if (!ctaText || !ctaDomain || !ctaSlug) {
      return null;
    }

    return {
      text: ctaText,
      href: `${ctaDomain}/${ctaSlug}`
    };
  };

  const buildEventEyebrow = event => {
    const sponsorString = event?.c_70548;
    const venueType = event.event_venue_type; // In Person, Virtual Only, Hybrid Only

    let cleanedVenueType = venueType.replace(' Only', '');
    cleanedVenueType = cleanedVenueType.replace('In Person', 'In-Person');

    if (sponsorString) return `${cleanedVenueType} | ${sponsorString}`;
    return cleanedVenueType;
  };

  const eventIsLive = event => {
    const isLiveCustomField = event?.c_70861;

    if (isLiveCustomField && isLiveCustomField[0]?.value === 'Yes') return true;
    return false;
  };

  const buildEventTimeString = (event, dateOnly = false) => {
    const { start_date } = event;
    let { start_time, end_time, timezone, end_date } = event;

    // These fields are all optional in the Swoogo dashboard - yet we need them to build the time string to match Figma
    // These are some default values to prevent errors when the dashboard is incomplete
    if (!start_time) start_time = '00:00:00';
    if (!end_time) end_time = '23:59:59';
    if (!timezone) timezone = 'US/Eastern';
    if (!end_date) end_date = start_date;

    // Configure dayjs to parse in the event's timezone
    dayjs.extend(utcPlugin);
    dayjs.extend(timezonePlugin);

    let dayjsStartDate = '';
    let dayjsEndDate = '';
    try {
      dayjsStartDate = dayjs.tz(`${start_date} ${start_time}`, timezone);
      dayjsEndDate = dayjs.tz(`${end_date} ${end_time}`, timezone);
    } catch (error) {
      // Data is incomplete in Swoogo, return empty string
      return '';
    }

    // Event that spans multiple days
    const isMultiDay = dayjsStartDate.day() !== dayjsEndDate.day();
    // Event that spans multiple months ex. Sept 30 - Oct 1
    const isMultiMonth = dayjsStartDate.month() !== dayjsEndDate.month();

    const getTimezoneAbr = () => {
      // Define short timezone format w/ event's timezone
      const formatter = new Intl.DateTimeFormat('en', {
        timeZoneName: 'short',
        timezone
      });

      // Parse current date/time w/ previously defined format, return only timezone abbreviation string
      return formatter
        .formatToParts(new Date())
        .find(part => part.type === 'timeZoneName').value;
    };

    const endMonth = dayjsEndDate.format('MMMM');
    const dayOfEnd = dayjsEndDate.format('DD');
    const dayOfWeek = dayjsStartDate.format('dddd');
    const startMonth = dayjsStartDate.format('MMMM');
    const dayOfStart = dayjsStartDate.format('DD');
    const year = dayjsStartDate.format('YYYY');
    const startTime = dayjsStartDate
      .format('h:mm a')
      .replace('am', 'a.m.')
      .replace('pm', 'p.m.');
    const endTime = dayjsEndDate
      .format('h:mm a')
      .replace('am', 'a.m.')
      .replace('pm', 'p.m.');

    const parseSingleDay = () => {
      /*
        * Single day event fmt
        * Tuesday, Month 00, 2023
        * 11:00 a.m. - 5:00 p.m. Eastern
      */

      if (dateOnly) {
        return `${dayOfWeek}, ${startMonth} ${dayOfStart}`;
      }

      return `${dayOfWeek}, ${startMonth} ${dayOfStart}, ${year}\n${startTime} - ${endTime} ${getTimezoneAbr()}`;
    };

    const parseMultiDay = () => {
      /*
        * Single day event fmt
        * Month 00-00, 2023
        * 11:00 a.m. - 5:00 p.m. Eastern
      */

      if (dateOnly) {
        return `${startMonth} ${dayOfStart}-${dayOfEnd}, ${year}`;
      }

      return `${startMonth} ${dayOfStart}-${dayOfEnd}, ${year}\n${startTime} - ${endTime} ${getTimezoneAbr()}`;
    };

    const parseMultiMonth = () => {
      /*
        * Single day event fmt
        * Month 00 - Month 00, 2023
        * 11:00 a.m. - 5:00 p.m. Eastern
      */

      if (dateOnly) {
        return `${startMonth} ${dayOfStart} - ${endMonth} ${dayOfEnd}, ${year}`;
      }

      return `${startMonth} ${dayOfStart} - ${endMonth} ${dayOfEnd}, ${year}\n${startTime} - ${endTime} ${getTimezoneAbr()}`;
    };

    let parsedString;
    if (isMultiMonth) {
      parsedString = parseMultiMonth();
    } else if (isMultiDay) {
      parsedString = parseMultiDay();
    } else {
      parsedString = parseSingleDay();
    }

    return parsedString;
  };

  return (
    <main className="eventsCalendar">
      <section className="events__header">
        <Slug slugName={pageTitle} className="category-page" />
        <p className="page-description">{pageDescription}</p>
      </section>

      {featuredEvents?.length > 0 && (
        <section className="eventsCalendar__featured">
          <FeatureCard
            imgSrc={{
              mobile: featuredEvents[0]?.c_75275,
              desktop: featuredEvents[0]?.c_70826
            }}
            status={eventIsLive(featuredEvents[0]) && <Badge status="live" />}
            eyebrow={buildEventEyebrow(featuredEvents[0])}
            head={
              <Title text={getEventName(featuredEvents[0])} xl element="h2" />
            }
            subhead={featuredEvents[0].c_70546}
            cta={buildEventCta(featuredEvents[0])}
            footnote={buildEventTimeString(featuredEvents[0], true)}
          />
        </section>
      )}

      <section className="eventsCalendar__upcoming">
        <Slug slugName="Upcoming" className="noBorder" />
        <CardList>
          {upcomingEvents.map(event => (
            <Card
              className="card"
              key={event.id}
              image={
                <>
                  <div className="card_image_container">
                    <img
                      src={event.c_70826}
                      alt={getEventName(event)}
                      loading="lazy"
                    />
                  </div>
                </>
              }
              status={eventIsLive(event) && <Badge status="live" />}
              eyebrow={buildEventEyebrow(event)}
              title={getEventName(event)}
              description={event.c_70546}
              href={`${event.domain}/${event.url}`}
              footnote={buildEventTimeString(event)}
              cta={buildEventCta(event)}
              ariaLabel={`Event registration for ${getEventName(event)}`}
              xl
            />
          ))}
        </CardList>
      </section>
      {pastEvents.length > 0 && (
        <section className="eventsCalendar__past">
          <Slug slugName="Videos From Past Events" className="noBorder" />
          <CardList>
            {pastEvents.map(video => {
              const [smImg, mdImg, lgImg] = video.thumbnailUrlResize;
              return (
                <Card
                  key={video.id}
                  image={
                    <>
                      <div className="card_image_container">
                        <img
                          src={video.thumbnailUrl}
                          alt={video.title}
                          srcSet={`${smImg} 810w, ${mdImg} 600w, ${lgImg} 420w`}
                          sizes="(max-width: 600px) calc(100vw - 60px), (max-width: 920px) calc(100vw / 2 - 60px), 280px"
                          loading="lazy"
                        />
                      </div>
                    </>
                  }
                  eyebrow={getEyebrowFromTags(video.tags)}
                  title={video.title}
                  description={video.description}
                  href={`/events/video/${video.slug}/${video.id}`}
                  showPlayIcon
                  ariaLabel={`Watch a video of a past event - ${video.title}`}
                />
              );
            })}
          </CardList>
        </section>
      )}
    </main>
  );
};

CalendarPageComponent.defaultProps = {
  events: [],
  pastEvents: []
};

CalendarPageComponent.propTypes = {
  events: PropTypes.arrayOf(PropTypes.object),
  pastEvents: PropTypes.arrayOf(PropTypes.object)
};

const mapStateToProps = state => ({
  events: state?.eventsPage?.data?.events?.items || [],
  pastEvents: state?.eventsPage?.data?.playlist?.slice(0, 20) || []
});

export default connect(mapStateToProps)(CalendarPageComponent);
